import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { GripVertical, ChevronUp, ChevronDown, Video, FileText, Music, Eye, Pencil, Trash2, RefreshCw, Download, FolderInput } from 'lucide-react';

/**
 * SortableFileItem - A draggable file item with reorder controls
 * 
 * Supports three reordering methods:
 * 1. Drag and drop (via @dnd-kit)
 * 2. Up/down arrow buttons
 * 3. Manual order number input
 */
const SortableFileItem = ({
  file,
  index,
  totalFiles,
  lessonVideoUrl,
  onMoveUp,
  onMoveDown,
  onOrderChange,
  onView,
  onRename,
  onReplace,
  onDownload,
  onDelete,
  onMove, // New prop for moving file to another course
  disableDrag = false, // Disable drag when search is active
  isOpened = false, // Sky-blue indicator for opened files
}) => {
  const [orderInput, setOrderInput] = useState('');
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  
  // Color scheme based on opened state
  // Opened: Sky blue - background #E1F5FE, border #29B6F6, accent #0288D1
  // Unopened: Green - background #E8F5E9, border #4CAF50, accent #2E7D32
  const colors = isOpened 
    ? { bg: '#E1F5FE', border: '#29B6F6', accent: '#0288D1' }
    : { bg: '#E8F5E9', border: '#4CAF50', accent: '#2E7D32' };
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.id, disabled: disableDrag });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return null;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };
  
  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = String(Math.floor(seconds % 60)).padStart(2, '0');
    return `${mins}:${secs}`;
  };
  
  const fileSize = formatFileSize(file.file_size);
  const duration = formatDuration(file.duration);
  const isAudio = file.file_type === 'audio';
  const isPdf = file.file_type === 'pdf';
  const isVideo = file.file_type === 'video';
  
  // Calculate display number (accounting for primary file if exists)
  const displayNumber = lessonVideoUrl ? index + 2 : index + 1;
  
  const handleOrderInputChange = (e) => {
    setOrderInput(e.target.value);
  };
  
  const handleOrderInputBlur = () => {
    const newOrder = parseInt(orderInput, 10);
    if (!isNaN(newOrder) && newOrder >= 1 && newOrder <= totalFiles) {
      // Convert from 1-based display to 0-based index
      const newIndex = newOrder - 1;
      if (newIndex !== index) {
        onOrderChange(file.id, newIndex);
      }
    }
    setIsEditingOrder(false);
    setOrderInput('');
  };
  
  const handleOrderInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleOrderInputBlur();
    } else if (e.key === 'Escape') {
      setIsEditingOrder(false);
      setOrderInput('');
    }
  };
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 rounded-lg ${isDragging ? 'shadow-lg' : ''}`}
      data-testid={`sortable-file-${file.id}`}
    >
      <div 
        className="p-3 rounded-lg"
        style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
      >
        {/* Row 1: File type icon, number, type label */}
        <div className="flex items-center gap-2">
          {/* File type icon */}
          {isPdf ? (
            <FileText className="h-5 w-5 flex-shrink-0" style={{ color: colors.accent }} />
          ) : isAudio ? (
            <Music className="h-5 w-5 flex-shrink-0" style={{ color: colors.accent }} />
          ) : (
            <Video className="h-5 w-5 flex-shrink-0" style={{ color: colors.accent }} />
          )}
          
          {/* Order number input/badge */}
          {isEditingOrder ? (
            <Input
              type="number"
              min="1"
              max={totalFiles}
              value={orderInput}
              onChange={handleOrderInputChange}
              onBlur={handleOrderInputBlur}
              onKeyDown={handleOrderInputKeyDown}
              autoFocus
              className="w-12 h-6 text-xs text-center p-1 flex-shrink-0"
              style={{ borderColor: colors.border }}
            />
          ) : (
            <Badge 
              onClick={() => {
                setIsEditingOrder(true);
                setOrderInput(String(index + 1));
              }}
              style={{ 
                backgroundColor: colors.accent, 
                color: 'white', 
                minWidth: '24px', 
                justifyContent: 'center',
                cursor: 'pointer'
              }} 
              className="text-xs font-bold flex-shrink-0"
              title="Click to change order"
            >
              #{displayNumber}
            </Badge>
          )}
          
          {/* File type label */}
          <Badge variant="outline" style={{ borderColor: colors.accent, color: colors.accent }} className="text-xs flex-shrink-0">
            {isPdf ? 'PDF' : isAudio ? 'Audio' : 'Video'}
          </Badge>
        </div>
        
        {/* Row 1.5: File size and duration (when present) */}
        {(fileSize || duration) && (
          <div className="mt-1">
            <span className="text-xs" style={{ color: '#5C635A' }}>
              {fileSize}{fileSize && duration ? ' • ' : ''}{duration}
            </span>
          </div>
        )}
        
        {/* Row 2: File name/description */}
        <div className="mt-2 py-2">
          <span className="font-medium text-sm break-words" style={{ color: colors.accent }}>
            {file.file_name || file.url?.split('/').pop() || 'File'}
          </span>
        </div>
        
        {/* Row 3: Action buttons with reorder controls */}
        <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t" style={{ borderColor: colors.border }}>
          {/* Drag handle */}
          <div
            {...(disableDrag ? {} : attributes)}
            {...(disableDrag ? {} : listeners)}
            className={`p-1 rounded flex items-center ${disableDrag ? 'cursor-not-allowed opacity-50' : 'cursor-grab active:cursor-grabbing'}`}
            style={{ backgroundColor: disableDrag ? 'transparent' : undefined }}
            title={disableDrag ? "Clear search to reorder" : "Drag to reorder"}
          >
            <GripVertical className="h-4 w-4" style={{ color: disableDrag ? '#ccc' : colors.accent }} />
          </div>
          
          {/* Up/Down arrows */}
          <div className="flex items-center gap-0.5 mr-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onMoveUp && onMoveUp(file.id)}
              disabled={disableDrag || !onMoveUp || index === 0}
              className="h-7 w-7 p-0"
              style={{ color: (disableDrag || !onMoveUp || index === 0) ? '#ccc' : colors.accent }}
              title={disableDrag ? "Clear search to reorder" : "Move up"}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onMoveDown && onMoveDown(file.id)}
              disabled={disableDrag || !onMoveDown || index === totalFiles - 1}
              className="h-7 w-7 p-0"
              style={{ color: (disableDrag || !onMoveDown || index === totalFiles - 1) ? '#ccc' : colors.accent }}
              title={disableDrag ? "Clear search to reorder" : "Move down"}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Separator */}
          <div className="h-5 w-px mx-1" style={{ backgroundColor: colors.border }} />
          
          {/* Action buttons */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onView(file)}
            className="flex-none h-7 px-2 max-w-[100px] text-xs"
            style={{ color: colors.accent, borderColor: colors.accent }}
            title={`View ${isPdf ? 'PDF' : isAudio ? 'Audio' : 'Video'}`}
          >
            <Eye className="h-3 w-3" />
            <span className="hidden sm:inline ml-1">View</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onRename(file)}
            className="flex-none h-7 px-2 max-w-[100px] text-xs"
            style={{ color: colors.accent, borderColor: colors.accent }}
            title="Rename file"
          >
            <Pencil className="h-3 w-3" />
            <span className="hidden sm:inline ml-1">Rename</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReplace(file)}
            className="flex-none h-7 px-2 max-w-[100px] text-xs"
            style={{ color: colors.accent, borderColor: colors.accent }}
            title="Replace file"
          >
            <RefreshCw className="h-3 w-3" />
            <span className="hidden sm:inline ml-1">Replace</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDownload(file)}
            className="flex-none h-7 px-2 max-w-[100px] text-xs"
            style={{ color: colors.accent, borderColor: colors.accent }}
            title="Download file"
          >
            <Download className="h-3 w-3" />
            <span className="hidden sm:inline ml-1">Download</span>
          </Button>
          {onMove && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onMove(file)}
              className="flex-none h-7 px-2 max-w-[100px] text-xs"
              style={{ color: '#0288D1', borderColor: '#0288D1' }}
              title="Move to another course"
            >
              <FolderInput className="h-3 w-3" />
              <span className="hidden sm:inline ml-1">Move</span>
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(file)}
            className="flex-none h-7 px-2 max-w-[100px] text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
            style={{ borderColor: '#ef4444' }}
            title="Delete file"
          >
            <Trash2 className="h-3 w-3" />
            <span className="hidden sm:inline ml-1">Delete</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SortableFileItem;
